<script lang="ts">
  import { api } from '../../framework/js/api.js';
  import { push } from 'svelte-spa-router';
  import { toast } from '../../framework/js/toast.js';

  let the_decision = '';
  let the_assumption = '';
  let the_stakes = '';
  let the_customer = '';
  let what_you_know = '';
  let time_budget = '';
  let submitting = false;

  async function handleSubmit(e: Event) {
    e.preventDefault();
    submitting = true;
    try {
      const body: any = await api.createAssessment({
        the_decision,
        the_assumption,
        the_stakes,
        the_customer,
        what_you_know,
        time_budget,
      });
      if (!body.success || !body.data?.assessment?.uuid) {
        toast.showNotification(false, body.message || 'Failed to save');
        return;
      }
      push(`/assessments/${body.data.assessment.uuid}`);
    } finally {
      submitting = false;
    }
  }
</script>

<div class="form-page">
  <div class="intro">
    <h1 class="page-title">Brief first</h1>
    <p class="page-sub">
      Do not edit yourself. Write as you think. We will structure it and tell you if research is actually needed.
    </p>
  </div>

  <form on:submit={handleSubmit} class="assessment-form">
    <section class="field-block">
      <div class="field-num">01</div>
      <div class="field-body">
        <h2 class="field-title">The decision</h2>
        <p class="field-prompt">What are you trying to decide in the next 3–6 months?</p>
        <textarea
          bind:value={the_decision}
          rows="4"
          class="field-input"
          placeholder="e.g. Whether to launch a new product line targeting a different age group than our current customers."
          aria-label="The decision"
        ></textarea>
      </div>
    </section>

    <section class="field-block">
      <div class="field-num">02</div>
      <div class="field-body">
        <h2 class="field-title">The assumption</h2>
        <p class="field-prompt">What do you believe to be true — but have not yet proven with data?</p>
        <p class="hint">Assumptions are unproven beliefs. They feel true, but you are not yet sure.</p>
        <textarea
          bind:value={the_assumption}
          rows="4"
          class="field-input"
          placeholder="e.g. I believe women 35+ in our region prefer natural skincare — but I have never validated this."
          aria-label="The assumption"
        ></textarea>
      </div>
    </section>

    <section class="field-block">
      <div class="field-num">03</div>
      <div class="field-body">
        <h2 class="field-title">The stakes</h2>
        <p class="field-prompt">What goes wrong if you get this one wrong?</p>
        <textarea
          bind:value={the_stakes}
          rows="3"
          class="field-input"
          placeholder="e.g. We would invest heavily in stock and a campaign that misses the target."
          aria-label="The stakes"
        ></textarea>
      </div>
    </section>

    <section class="field-block">
      <div class="field-num">04</div>
      <div class="field-body">
        <h2 class="field-title">The customer</h2>
        <p class="field-prompt">Who exactly are you making this decision for?</p>
        <textarea
          bind:value={the_customer}
          rows="3"
          class="field-input"
          placeholder="e.g. Small café owners aged 30–50 in metro areas who are time-poor."
          aria-label="The customer"
        ></textarea>
      </div>
    </section>

    <section class="field-block">
      <div class="field-num">05</div>
      <div class="field-body">
        <h2 class="field-title">What you know</h2>
        <p class="field-prompt">What confirmed facts or data points do you already have in hand?</p>
        <p class="hint">Known facts are things you can prove — data, sales figures, customer counts, confirmed feedback.</p>
        <textarea
          bind:value={what_you_know}
          rows="4"
          class="field-input"
          placeholder="e.g. We know our refund rate is 12% — we have the numbers."
          aria-label="What you know"
        ></textarea>
      </div>
    </section>

    <section class="field-block">
      <div class="field-num">06</div>
      <div class="field-body">
        <h2 class="field-title">Time &amp; budget</h2>
        <p class="field-prompt">How quickly do you need to move, and what is realistic to spend?</p>
        <textarea
          bind:value={time_budget}
          rows="3"
          class="field-input"
          placeholder="e.g. We need a decision in 6 weeks. We can spend up to $8,000 on research if it saves us from a bad call."
          aria-label="Time and budget"
        ></textarea>
      </div>
    </section>

    <div class="actions">
      <p class="ready">Ready for clarity?</p>
      <button type="submit" class="submit-btn" disabled={submitting}>
        {#if submitting}
          Saving…
        {:else}
          Save &amp; continue
        {/if}
      </button>
    </div>
  </form>
</div>

<style>
  .form-page {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 0 3rem;
  }

  .intro {
    margin-bottom: 2.5rem;
    text-align: left;
  }

  .page-title {
    font-size: 2rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 0.5rem 0;
  }

  .page-sub {
    color: var(--grey-secondary);
    margin: 0;
    line-height: 1.5;
  }

  .assessment-form {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .field-block {
    display: grid;
    grid-template-columns: 3rem 1fr;
    gap: 1rem;
    align-items: start;
  }

  @media (max-width: 768px) {
    .field-block {
      grid-template-columns: 1fr;
    }
    .field-num {
      font-size: 1.5rem !important;
    }
  }

  .field-num {
    font-size: 2rem;
    font-weight: 300;
    color: var(--grey-secondary);
    line-height: 1.2;
  }

  .field-title {
    font-size: 1.1rem;
    font-weight: 500;
    margin: 0 0 0.35rem 0;
    color: var(--grey);
  }

  .field-prompt {
    margin: 0 0 0.5rem 0;
    color: var(--grey-secondary);
    font-size: 0.95rem;
  }

  .hint {
    margin: 0 0 0.5rem 0;
    font-size: 0.85rem;
    color: var(--secondary);
  }

  .field-input {
    width: 100%;
    box-sizing: border-box;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-button);
    font-size: inherit;
    font-family: inherit;
    background-color: rgb(var(--input-bg));
    resize: vertical;
    min-height: 4rem;
  }

  .field-input:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(5, 108, 242, 0.1);
  }

  .actions {
    text-align: center;
    margin-top: 1rem;
  }

  .ready {
    margin-bottom: 1rem;
    color: var(--grey);
    font-weight: 400;
  }

  .submit-btn {
    border: 0;
    cursor: pointer;
    border-radius: 6px;
    padding: 12px 24px;
    font-weight: 500;
    background: var(--primary);
    color: var(--white);
    box-shadow: 1px 2px 3px rgba(0, 0, 0, 0.2);
  }

  .submit-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
</style>
